import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import {getPercent} from '@ey/utils';
import Utility from '../../../../../util/utilityfunctions';
import {
	arcReport,
	arcReportType,
	reportingFilterId,
	dashboardTabId,
	dashboardChartColors
} from '../../../../../util/uiconstants';
import {labels} from '../../../../../util/utils';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';

import {IconoirInfoCircle} from '@ey-xd/motif-icon';
import Icon from '@ey/canvascoreservices/Icon';

import Tile from '../../../../Common/Tile';
import PieChart from '../../../../Common/Charts/PieChart';
import FooterColumn from './FooterColumn';
import Tooltip from '../../../../CoreWrappers/Tooltip';
import StyledReportQualificationFooter from './StyledReportQualificationFooter';
import HrVert from '../../../../Common/Hr/HrVert';

const ReportQualification = (props) => {
	const {engagementMetrics} = props;

	const initialStats = {
		notSelected: [],
		unmodified: [],
		modified: [],
		adverse: [],
		disclaimer: [],
		qualification: [],
		total: 0
	};

	const [stats, setStats] = useState(initialStats);
	const {numberFormat} = useSelector((store) => store.userPreferences);
	const dispatch = useDispatch();

	useEffect(() => {
		engagementMetrics && calculateStats();
	}, []);

	useEffect(() => {
		engagementMetrics && calculateStats();
	}, [engagementMetrics]);

	const calculateStats = () => {
		const newStats = {...initialStats};

		engagementMetrics.forEach((em) => {
			// only account for engagements with a report date on or before current date.
			if (isReportDue(em)) {
				if (!em.arcRepQlf || em.arcRepQlf === arcReport.notApplicable) {
					newStats.notSelected = [...newStats.notSelected, em.engId];
				}

				if (em.arcRepQlf === arcReport.no) {
					newStats.unmodified = [...newStats.unmodified, em.engId];
				}

				if (em.arcRepQlf === arcReport.yes) {
					if ([arcReportType.adverse, arcReportType.adverseEoM].includes(em.arcRepQlfType)) {
						newStats.adverse = [...newStats.adverse, em.engId];
						newStats.modified = [...newStats.modified, em.engId];
					}

					if ([arcReportType.disclaimer, arcReportType.disclaimerEoM].includes(em.arcRepQlfType)) {
						newStats.disclaimer = [...newStats.disclaimer, em.engId];
						newStats.modified = [...newStats.modified, em.engId];
					}

					if ([arcReportType.qualified, arcReportType.qualifiedEoM].includes(em.arcRepQlfType)) {
						newStats.qualification = [...newStats.qualification, em.engId];
						newStats.modified = [...newStats.modified, em.engId];
					}
				}
			}
		});

		newStats.total =
			newStats.notSelected.length +
			newStats.unmodified.length +
			newStats.adverse.length +
			newStats.disclaimer.length +
			newStats.qualification.length;

		setStats(newStats);
	};

	const now = moment();
	const isReportDue = (engagement) => {
		return now.isSameOrAfter(engagement.relDate);
	};

	const tileFooter = !!stats.total && (
		<StyledReportQualificationFooter className="report-tile-footer">
			{[
				{
					header: Utility.getFormattedNumber(numberFormat, stats.modified.length),
					body: labels.modifiedOpinions,
					isTotal: true
				},
				{separator: true},
				{header: Utility.getFormattedNumber(numberFormat, stats.qualification.length), body: labels.qualified},
				{header: Utility.getFormattedNumber(numberFormat, stats.adverse.length), body: labels.adverse},
				{header: Utility.getFormattedNumber(numberFormat, stats.disclaimer.length), body: labels.disclaimer}
			].map((column, index) => {
				if (column.separator) return <HrVert />;
				return <FooterColumn {...column} key={`footer-column-${index}`} />;
			})}
		</StyledReportQualificationFooter>
	);

	const renderTooltip = () => {
		const renderTrigger = () => <Icon src={IconoirInfoCircle} />;

		const props = {
			trigger: renderTrigger(),
			tooltipText: labels.engagementReportTooltip,
			placement: 'left'
		};

		return <Tooltip {...props} />;
	};

	const onGraphicClickHandler = (highChartEvent) => {
		const opinionType = highChartEvent.point?.name;
		let filteredEngagementIds = [];

		switch (opinionType) {
			case labels.notSelected:
				filteredEngagementIds = stats.notSelected;
				break;
			case labels.unmodified:
				filteredEngagementIds = stats.unmodified;
				break;
			case labels.modified:
				filteredEngagementIds = stats.modified;
				break;
			case labels.adverse:
				filteredEngagementIds = stats.adverse;
				break;
			case labels.disclaimer:
				filteredEngagementIds = stats.disclaimer;
				break;
			case labels.qualification:
				filteredEngagementIds = stats.qualification;
				break;
		}

		const handlerOnClearFilter = () => {
			dispatch(clearFilterDashboardTiles(dashboardTabId.reporting));
		};

		const dashboardFilters = normalizeDashboardFilters(
			[
				{
					categoryLabel: opinionType,
					type: reportingFilterId.opinionType,
					onClearChip: handlerOnClearFilter
				}
			],
			filteredEngagementIds,
			handlerOnClearFilter
		);

		dispatch(updateFilterDashboardTiles(dashboardTabId.reporting, dashboardFilters));
	};

	const pieProps = {
		labelCenterOffset: 25,
		legendWidth: 150,
		data: [
			{
				name: labels.notSelected,
				y: getPercent(stats.notSelected.length, stats.total),
				totalCount: stats.notSelected.length,
				color: dashboardChartColors.blue[0]
			},
			{
				name: labels.unmodified,
				y: getPercent(stats.unmodified.length, stats.total),
				totalCount: stats.unmodified.length,
				color: dashboardChartColors.blue[2]
			},
			{
				name: labels.modified,
				y: getPercent(stats.modified.length, stats.total),
				totalCount: stats.modified.length,
				color: dashboardChartColors.red[2]
			}
		],
		total: stats.total,
		totalText: labels.opinions,
		onGraphicClick: onGraphicClickHandler
	};

	const tileProps = {
		title: labels.opinionType,
		subTitle: labels.basedOnSelectionArchiveForm,
		header: renderTooltip(),
		footer: tileFooter,
		isEmpty: stats.total === 0,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	return (
		<Tile {...tileProps}>
			<PieChart {...pieProps} />
		</Tile>
	);
};

ReportQualification.defaultProps = {
	engagementMetrics: []
};

ReportQualification.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			relDate: PropTypes.string,
			arcRepQlf: PropTypes.number,
			arcRepQlfType: PropTypes.number
		})
	)
};

export default ReportQualification;
